import { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import { toast } from "sonner";
import heic2any from "heic2any";

import MainContainer from "../../components/MainContainer";
import SeoHead from "../../components/Seo/SeoHead.js";
import ExplosionButton from "../../components/ExplosionButton/ExplosionButton";
import EnterPromptModal from "../../components/EnterPromptModal/EnterPromptModal";
import CropModal from "../../components/CropModal/CropModal";
import LottieIcon from "../../components/LottieIcon/LottieIcon";
import GenerationToast from "../../components/utils/GenerationToast";

import {
  Page,
  TopRow,
  BackButton,
  Hero,
  Eyebrow,
  Title,
  IntroText,
  HeroSkeleton,
  Grid,
  PreviewCard,
  SectionLabel,
  PreviewMediaWrap,
  MainImg,
  MainVideo,
  PreviewOverlay,
  InfoBlock,
  Tags,
  Tag,
  InfoRow,
  InfoLabel,
  InfoValue,
  ComposeCard,
  TabsContainer,
  TabButton,
  TabText,
  UploadContainer,
  UploadEmpty,
  UploadIconCircle,
  UploadHint,
  UploadSubHint,
  UploadedImage,
  UploadedVideo,
  UploadOverlayBtn,
  PreviewDeleteBtn,
  PreviewCropBtn,
  OptionsGroup,
  OptionRow,
  OptionText,
  ToggleTrack,
  ToggleThumb,
  GenerateRow,
  StyledIconWrapper,
  InfoIconWrapper,
  TooltipContainer,
  TooltipText,
  HowToSection,
  HowToTitle,
  HowToGrid,
  HowToStep,
  HowToStepNum,
  HowToStepText,
  RelatedSection,
  RelatedHeader,
  RelatedTitle,
  BrowseMoreLink,
  RelatedGrid,
  RelatedCard,
  RelatedCardEyebrow,
  RelatedCardTitle,
  RelatedCardArrow,
  SkeletonBox,
} from "./CollectionDetailsPage.styles";

import { ReactComponent as ChevronLeft } from "../../assets/icons/chevronLeft.svg";
import { ReactComponent as ImageIcon } from "../../assets/icons/image.svg";
import { ReactComponent as VideoRecorderIcon } from "../../assets/icons/video-recorder.svg";
import { ReactComponent as InfoIcon } from "../../assets/icons/info-circle.svg";
import { ReactComponent as UploadCloudIcon } from "../../assets/icons/uploadSvg.svg";
import { ReactComponent as CropIcon } from "../../assets/icons/crop.svg";

import deleteAnimation from "../../assets/animations/trash.json";
import settingsAnimation from "../../assets/animations/settings.json";

import { formatModelName } from "../../utils/utils";
import {
  DEFAULT_VIDEO_DURATION,
  DEFAULT_VIDEO_FPS,
  DEFAULT_VIDEO_RESOLUTION,
  MAX_IMAGE_SIZE,
  MAX_VIDEO_SIZE,
  NUDIFY_PRICE,
  UPSCALE_PRICE,
  VIDEO_DURATION_PRICE,
  VIDEO_FPS_PRICE,
  VIDEO_RESOLUTION_PRICE,
} from "../../utils/constants";

import {
  fetchCollection,
  generateCollection,
} from "../../store/slices/model/asyncThunks";
import {
  selectCollectionDetails,
  selectGenerateCollection,
} from "../../store/slices/model/slice";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import {
  setSignUpModalOpen,
  selectAutoPlay,
} from "../../store/slices/common/slice";

import {
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildPageTitle,
  buildWebApplicationSchema,
} from "../../seo/siteSeo.js";
import { videoTemplatesBySlug } from "../../seo/templates.js";

const MODAL_CONTENT = {
  IMAGE_TO_VIDEO: "IMAGE_TO_VIDEO",
  EXTEND_VIDEO: "EXTEND_VIDEO",
};

const CollectionDetailsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { slug } = useParams();

  const template = videoTemplatesBySlug[slug] || null;
  const collectionId = template?.collectionId || slug;

  const { data: collectionDetailsData, loading } = useSelector(
    selectCollectionDetails,
  );
  const { loading: generateVideoLoading } = useSelector(
    selectGenerateCollection,
  );
  const loggedIn = useSelector(selectLoggedIn);
  const autoPlay = useSelector(selectAutoPlay);

  const [isExploding, setIsExploding] = useState(false);

  const data =
    collectionDetailsData?._id === collectionId ? collectionDetailsData : null;
  const isInitialCollectionLoading = loading && !data;

  const displayImage =
    data?.input?.image?.url || data?.input?.image?.resizedImage?.url;
  const displayOutputVideo =
    data?.output?.video?.compressedUrl || data?.output?.video?.url;

  const [selectedTab, setSelectedTab] = useState(MODAL_CONTENT.IMAGE_TO_VIDEO);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [nudifyEnabled, setNudifyEnabled] = useState(false);
  const [upscaleEnabled, setUpscaleEnabled] = useState(false);

  const [showTooltip, setShowTooltip] = useState(false);

  const [customPrompt, setCustomPrompt] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [seedNumber, setSeedNumber] = useState(
    Math.floor(Math.random() * 10000000) + 1,
  );
  const [showPrompts, setShowPrompts] = useState(false);
  const [videoResolution, setVideoResolution] = useState(
    localStorage.getItem("videoResolution") ?? DEFAULT_VIDEO_RESOLUTION,
  );
  const [fps, setFps] = useState(
    Number(localStorage.getItem("videoFps") ?? DEFAULT_VIDEO_FPS),
  );
  const [videoDuration, setVideoDuration] = useState(
    Number(localStorage.getItem("videoDuration") ?? DEFAULT_VIDEO_DURATION),
  );

  const [isEnterPromptModalOpen, setIsEnterPromptModalOpen] = useState(false);
  const [isOpenCropModal, setIsOpenCropModal] = useState(false);

  const imageInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const tooltipRef = useRef(null);
  const mainVideoRef = useRef(null);

  const isMobile = useMediaQuery({ maxWidth: 768 });

  const [aspectRatio, setAspectRatio] = useState(0.7293447293447294);

  const collectionPrice = useMemo(() => {
    if (!data) return 0;

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      return (
        (data.price ?? 0) +
        (nudifyEnabled ? NUDIFY_PRICE : 0) +
        (upscaleEnabled ? UPSCALE_PRICE : 0) +
        (VIDEO_RESOLUTION_PRICE?.[videoResolution] ?? 0) +
        (VIDEO_DURATION_PRICE?.[videoDuration] ?? 0) +
        (VIDEO_FPS_PRICE?.[fps] ?? 0)
      );
    }

    return (
      (data.price ?? 0) +
      (VIDEO_RESOLUTION_PRICE?.[videoResolution] ?? 0) +
      (VIDEO_DURATION_PRICE?.[videoDuration] ?? 0) +
      (VIDEO_FPS_PRICE?.[fps] ?? 0)
    );
  }, [
    data,
    selectedTab,
    nudifyEnabled,
    upscaleEnabled,
    videoResolution,
    videoDuration,
    fps,
  ]);

  useEffect(() => {
    setAspectRatio(0.7293447293447294);
    setUploadedImage(null);
    setUploadedVideo(null);
    setNudifyEnabled(false);
    setUpscaleEnabled(false);
    setCustomPrompt("");
    setNegativePrompt("");
    setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO);
  }, [slug]);

  useEffect(() => {
    const width = data?.input?.image?.resizedImage?.width;
    const height = data?.input?.image?.resizedImage?.height;
    if (width && height) setAspectRatio(width / height);
  }, [data]);

  useEffect(() => {
    if (!mainVideoRef.current) return;
    if (autoPlay) mainVideoRef.current.play().catch(() => {});
    else mainVideoRef.current.pause();
  }, [autoPlay]);

  useEffect(() => {
    if (collectionId) {
      dispatch(fetchCollection({ collectionId }));
    }
  }, [dispatch, collectionId]);

  useEffect(() => {
    if (!showTooltip) return;

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setShowTooltip(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [showTooltip]);

  const handleBack = () => navigate(-1);

  const handleImageUpload = async (file) => {
    if (
      ![
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
        "image/heic",
        "image/heif",
      ].includes(file.type)
    ) {
      toast.error("Unsupported media type.");
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error("Image must be smaller than 20MB");
      return;
    }

    let processedFile = file;
    let blobToShow = file;

    if (file.type === "image/heic" || file.type === "image/heif") {
      try {
        const convertedBlob = await heic2any({
          blob: file,
          toType: "image/jpg",
          quality: 0.9,
        });
        blobToShow = convertedBlob;
        processedFile = new File(
          [convertedBlob],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          { type: "image/jpg", lastModified: Date.now() },
        );
      } catch (error) {
        console.error("HEIC -> jpg conversion failed", error);
        toast.error("Failed to process HEIC image");
        return;
      }
    }

    setUploadedImage({
      file: processedFile,
      url: URL.createObjectURL(blobToShow),
    });
  };

  const handleVideoUpload = (file) => {
    if (
      !(file.type === "video/mp4" || file.type.startsWith("video/quicktime"))
    ) {
      toast.error("Unsupported media type.");
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error("Video must be smaller than 30MB");
      return;
    }
    setUploadedVideo({ file, url: URL.createObjectURL(file) });
  };

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = "";
      return;
    }
    await handleImageUpload(file);
    e.target.value = "";
  };

  const handleVideoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      e.target.value = "";
      return;
    }
    handleVideoUpload(file);
    e.target.value = "";
  };

  const resetGenerationState = () => {
    setUploadedImage(null);
    setUploadedVideo(null);
    setNudifyEnabled(false);
    setUpscaleEnabled(false);
    setCustomPrompt("");
    setNegativePrompt("");
    setSeedNumber(Math.floor(Math.random() * 10000000) + 1);
    setShowPrompts(false);
  };

  const handleGenerate = () => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }
    if (generateVideoLoading || !data) return;

    const payload = {
      customPrompt,
      negativePrompt,
      modelId: data.model,
      resolution: videoResolution,
      seed: seedNumber,
      fps,
      videoDuration,
    };

    if (selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO) {
      payload.image = uploadedImage?.blob
        ? {
            file: new File([uploadedImage.blob], `cropped-${Date.now()}.jpg`, {
              type: "image/jpeg",
            }),
            url: uploadedImage.url,
          }
        : uploadedImage;
      payload.nudify = nudifyEnabled;
      payload.upscale = upscaleEnabled;
    } else {
      payload.video = uploadedVideo;
    }

    dispatch(generateCollection(payload))
      .unwrap()
      .then(() => {
        setIsExploding(true);
        setTimeout(() => setIsExploding(false), 600);

        toast.custom(() => (
          <GenerationToast
            successMessage={
              selectedTab === MODAL_CONTENT.EXTEND_VIDEO
                ? "Extending started..."
                : "Generation started..."
            }
            label="View"
            onClick={() => {
              navigate("/mine");
              window.scrollTo({ top: 0, left: 0, behavior: "instant" });
            }}
          />
        ));
        resetGenerationState();
      })
      .catch((err) => toast.error(err));
  };

  const buttonLabel =
    selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? "Generate" : "Extend";

  const videoTooltipContent =
    selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
      ? [
          "The uploaded image will be used as the first frame of the generated video.",
          "For best results, match the original image. The AI follows basic physics — objects not in the input image are unlikely to appear in the generated video.",
        ]
      : [
          "Extending a video reuses its last frame as the starting point for the new generation, making the final video longer and seamless.",
        ];

  const relatedTemplates = useMemo(() => {
    if (!template?.relatedSlugs?.length) return [];
    return template.relatedSlugs
      .map((relatedSlug) => videoTemplatesBySlug[relatedSlug])
      .filter(Boolean);
  }, [template]);

  const jsonLd = useMemo(() => {
    if (!template) return [];
    return [
      buildWebApplicationSchema({
        name: `${template.h1} | Fapify`,
        description: template.description,
        url: `https://www.fapify.com/template/${template.slug}`,
      }),
      buildBreadcrumbSchema([
        { name: "Home", path: "/" },
        { name: template.h1, path: `/template/${template.slug}` },
      ]),
      buildFaqSchema([
        {
          question: "How do I use this AI video template?",
          answer: template.howToSteps.join(" "),
        },
      ]),
    ];
  }, [template]);

  const pageTitle = template
    ? buildPageTitle(template.title)
    : data?.name
      ? buildPageTitle(`${formatModelName(data.name)} AI Video Template`)
      : buildPageTitle("AI Video Template");

  const pageDescription =
    template?.description ||
    data?.description ||
    "Use a reusable AI video template in Fapify, refine the frame, and generate a short clip.";

  return (
    <MainContainer>
      <SeoHead
        title={pageTitle}
        description={pageDescription}
        path={`/template/${slug}`}
        keywords={template?.keywords}
        jsonLd={jsonLd}
        noindex={!template}
      />

      <Page>
        <TopRow>
          <BackButton onClick={handleBack}>
            <ChevronLeft />
            Back
          </BackButton>
        </TopRow>

        <Hero>
          <Eyebrow>AI Video Template</Eyebrow>
          {isInitialCollectionLoading && !template ? (
            <HeroSkeleton>
              <SkeletonBox $width="68%" $height="36px" $radius="10px" />
              <SkeletonBox
                $width="42%"
                $height="28px"
                $radius="10px"
                style={{ marginTop: 6 }}
              />
              <SkeletonBox
                $width="92%"
                $height="14px"
                $radius="8px"
                style={{ marginTop: 14 }}
              />
              <SkeletonBox
                $width="76%"
                $height="14px"
                $radius="8px"
                style={{ marginTop: 6 }}
              />
            </HeroSkeleton>
          ) : (
            <>
              <Title>
                {template?.h1 ||
                  (data?.name
                    ? `${formatModelName(data.name)} AI Video Template`
                    : "AI Video Template")}
              </Title>
              {(template?.intro || data?.description) && (
                <IntroText>{template?.intro || data?.description}</IntroText>
              )}
            </>
          )}
        </Hero>

        <Grid>
          <PreviewCard>
            <SectionLabel>After — example result</SectionLabel>
            <PreviewMediaWrap $aspectRatio={aspectRatio}>
              {displayOutputVideo ? (
                <MainVideo
                  ref={mainVideoRef}
                  src={displayOutputVideo}
                  poster={displayImage}
                  loop
                  playsInline
                  autoPlay={autoPlay}
                  muted
                />
              ) : displayImage ? (
                <MainImg src={displayImage} alt={data?.name || template?.h1} />
              ) : (
                <SkeletonBox $radius="18px" $aspectRatio={aspectRatio} />
              )}
              <PreviewOverlay />
            </PreviewMediaWrap>

            <InfoBlock>
              {data?.keywords?.length > 0 && (
                <Tags>
                  {data.keywords.map((tag, i) => (
                    <Tag key={i}>{tag}</Tag>
                  ))}
                </Tags>
              )}

              {data?.description && (
                <InfoRow>
                  <InfoLabel>Description</InfoLabel>
                  <InfoValue>{data.description}</InfoValue>
                </InfoRow>
              )}
            </InfoBlock>
          </PreviewCard>

          <ComposeCard>
            <SectionLabel>Before — your upload</SectionLabel>

            <TabsContainer $activeTab={selectedTab}>
              <TabButton
                onClick={() => setSelectedTab(MODAL_CONTENT.IMAGE_TO_VIDEO)}
                $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
              >
                <ImageIcon width={16} height={16} />
                <TabText
                  $selected={selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO}
                >
                  Image to Video
                </TabText>
              </TabButton>
              <TabButton
                onClick={() => setSelectedTab(MODAL_CONTENT.EXTEND_VIDEO)}
                $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}
              >
                <VideoRecorderIcon width={16} height={16} />
                <TabText $selected={selectedTab === MODAL_CONTENT.EXTEND_VIDEO}>
                  Extend Video
                </TabText>
              </TabButton>
            </TabsContainer>

            {selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO ? (
              <>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                  onChange={handleImageFileChange}
                  style={{ display: "none" }}
                />
                <UploadContainer
                  onClick={() =>
                    !uploadedImage && imageInputRef.current?.click()
                  }
                >
                  {uploadedImage ? (
                    <>
                      <PreviewDeleteBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedImage(null);
                        }}
                      >
                        <LottieIcon
                          animationData={deleteAnimation}
                          trigger="hover"
                          size={24}
                        />
                      </PreviewDeleteBtn>
                      <PreviewCropBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsOpenCropModal(true);
                        }}
                      >
                        <CropIcon />
                      </PreviewCropBtn>
                      <UploadedImage
                        src={uploadedImage.url}
                        alt="Uploaded preview"
                      />
                      <UploadOverlayBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          imageInputRef.current?.click();
                        }}
                      >
                        Change Image
                      </UploadOverlayBtn>
                    </>
                  ) : (
                    <UploadEmpty>
                      <UploadIconCircle>
                        <UploadCloudIcon width={22} height={22} />
                      </UploadIconCircle>
                      <UploadHint>Upload an image to start</UploadHint>
                      <UploadSubHint>
                        JPG, PNG or WEBP — up to 20 MB
                      </UploadSubHint>
                      <UploadOverlayBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          imageInputRef.current?.click();
                        }}
                      >
                        Upload Image
                      </UploadOverlayBtn>
                    </UploadEmpty>
                  )}
                </UploadContainer>

                <OptionsGroup>
                  <OptionRow onClick={() => setNudifyEnabled(!nudifyEnabled)}>
                    <OptionText>Nudify before scene</OptionText>
                    <ToggleTrack $active={nudifyEnabled}>
                      <ToggleThumb $active={nudifyEnabled} />
                    </ToggleTrack>
                  </OptionRow>
                  <OptionRow onClick={() => setUpscaleEnabled(!upscaleEnabled)}>
                    <OptionText>Upscale</OptionText>
                    <ToggleTrack $active={upscaleEnabled}>
                      <ToggleThumb $active={upscaleEnabled} />
                    </ToggleTrack>
                  </OptionRow>
                </OptionsGroup>
              </>
            ) : (
              <>
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  onChange={handleVideoFileChange}
                  style={{ display: "none" }}
                />
                <UploadContainer
                  onClick={() =>
                    !uploadedVideo && videoInputRef.current?.click()
                  }
                >
                  {uploadedVideo ? (
                    <>
                      <PreviewDeleteBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          setUploadedVideo(null);
                        }}
                      >
                        <LottieIcon
                          animationData={deleteAnimation}
                          trigger="hover"
                          size={24}
                        />
                      </PreviewDeleteBtn>
                      <UploadedVideo
                        src={uploadedVideo.url}
                        loop
                        muted
                        autoPlay
                        playsInline
                      />
                      <UploadOverlayBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          videoInputRef.current?.click();
                        }}
                      >
                        Change Video
                      </UploadOverlayBtn>
                    </>
                  ) : (
                    <UploadEmpty>
                      <UploadIconCircle>
                        <UploadCloudIcon width={22} height={22} />
                      </UploadIconCircle>
                      <UploadHint>Upload a video to extend</UploadHint>
                      <UploadSubHint>MP4 or MOV — up to 30 MB</UploadSubHint>
                      <UploadOverlayBtn
                        onClick={(e) => {
                          e.stopPropagation();
                          videoInputRef.current?.click();
                        }}
                      >
                        Upload Video
                      </UploadOverlayBtn>
                    </UploadEmpty>
                  )}
                </UploadContainer>
              </>
            )}

            <GenerateRow>
              <StyledIconWrapper
                onClick={() => {
                  setIsEnterPromptModalOpen(true);
                  localStorage.setItem("wasPromptModalOpen", true);
                }}
              >
                <LottieIcon
                  animationData={settingsAnimation}
                  trigger="hover"
                  strokeColor={"#fff"}
                  size={24}
                />
              </StyledIconWrapper>

              <ExplosionButton
                disabled={generateVideoLoading || !data}
                onClick={handleGenerate}
                isLoading={generateVideoLoading}
                isExploding={isExploding}
                style={{ flex: 1, width: "100%", minWidth: 0 }}
                small
                white
              >
                {generateVideoLoading ? (
                  <span>Generating…</span>
                ) : (
                  <>
                    {buttonLabel}
                    <span style={{ fontSize: 16, lineHeight: 1 }}>💦</span>
                    {Math.ceil(collectionPrice)}
                  </>
                )}
              </ExplosionButton>

              <InfoIconWrapper
                ref={tooltipRef}
                onMouseEnter={() => {
                  if (!isMobile) setShowTooltip(true);
                }}
                onMouseLeave={() => {
                  if (!isMobile) setShowTooltip(false);
                }}
                onClick={() => {
                  if (isMobile) setShowTooltip(!showTooltip);
                }}
              >
                <InfoIcon width={18} height={18} />
                {showTooltip && (
                  <TooltipContainer>
                    {videoTooltipContent.map((text, i) => (
                      <TooltipText key={i}>{text}</TooltipText>
                    ))}
                    {data?.description && (
                      <>
                        <TooltipText $light>Tips</TooltipText>
                        <TooltipText>{data.description}</TooltipText>
                      </>
                    )}
                  </TooltipContainer>
                )}
              </InfoIconWrapper>
            </GenerateRow>
          </ComposeCard>
        </Grid>

        {template?.howToSteps?.length > 0 && (
          <HowToSection>
            <HowToTitle>How to use this template</HowToTitle>
            <HowToGrid>
              {template.howToSteps.map((step, i) => (
                <HowToStep key={i}>
                  <HowToStepNum>{i + 1}</HowToStepNum>
                  <HowToStepText>{step}</HowToStepText>
                </HowToStep>
              ))}
            </HowToGrid>
          </HowToSection>
        )}

        {relatedTemplates.length > 0 && (
          <RelatedSection>
            <RelatedHeader>
              <RelatedTitle>More templates you might like</RelatedTitle>
              <BrowseMoreLink to="/">
                Browse all video templates →
              </BrowseMoreLink>
            </RelatedHeader>
            <RelatedGrid>
              {relatedTemplates.map((item) => (
                <RelatedCard key={item.slug} to={`/template/${item.slug}`}>
                  <RelatedCardEyebrow>Template</RelatedCardEyebrow>
                  <RelatedCardTitle>{item.h1}</RelatedCardTitle>
                  <RelatedCardArrow>Open template →</RelatedCardArrow>
                </RelatedCard>
              ))}
            </RelatedGrid>
          </RelatedSection>
        )}
      </Page>

      <EnterPromptModal
        isOpen={isEnterPromptModalOpen}
        onClose={() => setIsEnterPromptModalOpen(false)}
        customPrompt={customPrompt}
        setCustomPrompt={setCustomPrompt}
        negativePrompt={negativePrompt}
        setNegativePrompt={setNegativePrompt}
        videoResolution={videoResolution}
        setVideoResolution={setVideoResolution}
        enableVideoQuality
        modelPrice={
          selectedTab === MODAL_CONTENT.IMAGE_TO_VIDEO
            ? (data?.price ?? 0) + (nudifyEnabled ? NUDIFY_PRICE : 0)
            : (data?.price ?? 0)
        }
        setCustomSeed={setSeedNumber}
        customSeed={seedNumber}
        collectionSeed={data?.seed}
        fps={fps}
        setFps={setFps}
        videoDuration={videoDuration}
        setVideoDuration={setVideoDuration}
        setShowPrompts={setShowPrompts}
        showPrompts={showPrompts}
        isMinVideoLength8sec={data?.output?.videoDuration === 8}
        collectionId={data?._id}
      />

      <CropModal
        selectedImage={uploadedImage}
        onClose={() => setIsOpenCropModal(false)}
        onConfirm={(croppedImageData) => {
          setUploadedImage({
            file: new File(
              [croppedImageData.blob],
              `cropped-${Date.now()}.jpg`,
              { type: "image/jpeg" },
            ),
            url: croppedImageData.url,
            blob: croppedImageData.blob,
          });
        }}
        isOpen={isOpenCropModal}
      />
    </MainContainer>
  );
};

export default CollectionDetailsPage;
